import { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import Rating from "@mui/material/Rating";
import { publicRequest } from "../requestMethods";
import theme from "../theme";
import { useToast } from "../ToastContext";
import {
  IconDocument, IconEdit, IconTrash,
  IconHeart, IconMessage, IconFolder, IconPaperclip,
  IconClipboard, IconCheck, IconExternalLink, IconClock, IconEye,
} from "../icons";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ── Card ── */
const Card = styled.div`
  background: ${theme.cardBg};
  border-radius: 14px;
  border: 1.5px solid ${theme.cardBorder};
  box-shadow: 0 1px 6px rgba(26, 18, 8, 0.05);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: transform 0.2s ease, box-shadow 0.2s;
  animation: ${fadeUp} 0.3s ease both;
  cursor: default;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 24px rgba(26, 18, 8, 0.1);
  }
`;

/* ── Author row ── */
const AuthorRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 10px;
`;

const AuthorLeft = styled(Link)`
  display: flex;
  align-items: center;
  gap: 9px;
  text-decoration: none;
`;

const Avatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${theme.bgAlt};
  border: 1.5px solid ${theme.cardBorder};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: ${theme.primary};
  flex-shrink: 0;
`;

const AvatarImg = styled.img`
  width: 100%; height: 100%; object-fit: cover;
`;

const AuthorName = styled.span`
  font-size: 12.5px;
  font-weight: 600;
  color: ${theme.textMuted};
  transition: color 0.15s;
  ${AuthorLeft}:hover & { color: ${theme.primary}; }
`;

const OwnerActions = styled.div`
  display: flex;
  gap: 4px;
`;

const SmallBtn = styled.button`
  background: transparent;
  border: 1.5px solid ${theme.cardBorder};
  border-radius: 6px;
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  color: ${(p) => (p.$danger ? theme.danger : theme.textMuted)};
  display: flex;
  align-items: center;
  gap: 4px;
  transition: color 0.15s, border-color 0.15s, background 0.15s;
  &:hover {
    color: ${(p) => (p.$danger ? theme.danger : theme.primary)};
    border-color: ${(p) => (p.$danger ? theme.danger : theme.primary)};
    background: ${(p) => (p.$danger ? theme.dangerLight : theme.primaryLight)};
  }
`;

/* ── Thumbnail ── */
const Thumbnail = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  display: block;
`;

const ThumbnailPlaceholder = styled.div`
  width: 100%;
  height: 200px;
  background: ${theme.bgAlt};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.cardBorderStrong};
`;

/* ── Body ── */
const Body = styled.div`
  padding: 14px 16px 10px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const NoteTitle = styled.h3`
  font-size: 15px;
  font-weight: 700;
  color: ${theme.textMain};
  margin: 0;
  line-height: 1.35;
  letter-spacing: -0.2px;
`;

const NoteDesc = styled.p`
  font-size: 12.5px;
  color: ${theme.textMuted};
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.55;
`;

/* ── Edit form ── */
const EditForm = styled.form`
  padding: 12px 16px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-top: 1px solid ${theme.separator};
`;

const EditInput = styled.input`
  width: 100%;
  background: ${theme.bg};
  border: 1.5px solid ${theme.cardBorder};
  border-radius: 7px;
  padding: 7px 11px;
  font-size: 13px;
  font-family: inherit;
  color: ${theme.textMain};
  outline: none;
  box-sizing: border-box;
  &:focus { border-color: ${theme.primary}; box-shadow: 0 0 0 3px ${theme.primaryGlow}; }
`;

const EditTextarea = styled.textarea`
  width: 100%;
  background: ${theme.bg};
  border: 1.5px solid ${theme.cardBorder};
  border-radius: 7px;
  padding: 7px 11px;
  font-size: 13px;
  font-family: inherit;
  color: ${theme.textMain};
  outline: none;
  resize: vertical;
  min-height: 56px;
  box-sizing: border-box;
  &:focus { border-color: ${theme.primary}; box-shadow: 0 0 0 3px ${theme.primaryGlow}; }
`;

const EditRow = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const SaveBtn = styled.button`
  background: ${theme.primary};
  color: #fff;
  border: none;
  border-radius: 7px;
  padding: 6px 18px;
  font-size: 12.5px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  &:disabled { opacity: 0.55; cursor: not-allowed; }
  &:not(:disabled):hover { background: ${theme.primaryDark}; }
`;

const CancelBtn = styled(SaveBtn)`
  background: transparent;
  color: ${theme.textMuted};
  border: 1.5px solid ${theme.cardBorder};
  &:hover { background: ${theme.bgAlt}; color: ${theme.textMain}; }
`;

/* ── Footer ── */
const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px 12px;
  border-top: 1px solid ${theme.separator};
  margin-top: auto;
`;

const Stat = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 500;
  font-family: inherit;
  color: ${(p) => (p.$active ? theme.danger : theme.textMuted)};
  background: transparent;
  border: none;
  padding: 0;
  cursor: ${(p) => (p.onClick ? "pointer" : "default")};
  transition: color 0.15s;
  &:hover { color: ${(p) => (p.$heart ? theme.danger : p.$comment ? theme.primary : theme.textMuted)}; }
`;

const ViewBtn = styled.button`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11.5px;
  font-weight: 600;
  font-family: inherit;
  color: ${theme.primary};
  background: ${theme.primaryLight};
  border: none;
  border-radius: 6px;
  padding: 4px 10px;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: rgba(160, 98, 10, 0.18); }
`;

const RatingBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: ${theme.primaryLight};
  border-radius: 6px;
  padding: 3px 8px;
  font-size: 12px;
  font-weight: 700;
  color: ${theme.primary};
  white-space: nowrap;
`;

/* ── Rating row (unrated) ── */
const RateRow = styled.div`
  padding: 8px 16px 12px;
  border-top: 1px solid ${theme.separator};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RateLabel = styled.span`
  font-size: 11.5px;
  color: ${theme.textMuted};
`;

const TimeStamp = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  color: ${theme.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-left: auto;
`;

/* ── Context menu ── */
const CtxMenu = styled.div`
  position: fixed;
  background: ${theme.cardBg};
  border: 1.5px solid ${theme.cardBorder};
  border-radius: 10px;
  box-shadow: 0 8px 28px rgba(26, 18, 8, 0.14);
  padding: 5px 0;
  z-index: 1000;
  min-width: 200px;
`;

const CtxTitle = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: ${theme.textMuted};
  padding: 6px 14px 4px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const CtxItem = styled.button`
  width: 100%;
  text-align: left;
  background: transparent;
  border: none;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;
  color: ${theme.textMain};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.1s, color 0.1s;
  &:hover { background: ${theme.bg}; color: ${theme.primary}; }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const CtxDivider = styled.div`
  height: 1px;
  background: ${theme.separator};
  margin: 4px 0;
`;

/* ── Preview popup ── */
const PopupOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(26, 18, 8, 0.48);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 600;
  padding: 20px;
  backdrop-filter: blur(3px);
`;

const PopupBox = styled.div`
  background: ${theme.cardBg};
  border-radius: 14px;
  border: 1.5px solid ${theme.cardBorder};
  box-shadow: 0 24px 64px rgba(26, 18, 8, 0.2);
  width: 100%;
  max-width: 640px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${fadeUp} 0.25s ease both;
`;

const PopupBody = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  min-height: 300px;
`;

const PopupLeft = styled.div`
  width: 150px;
  flex-shrink: 0;
  border-right: 1px solid ${theme.separator};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 12px 16px;
  gap: 10px;
  background: ${theme.bgAlt};
`;

const PopupThumb = styled.img`
  width: 116px; height: 136px; object-fit: cover; border-radius: 8px; border: 1.5px solid ${theme.cardBorder};
`;

const PopupThumbPlaceholder = styled.div`
  width: 116px; height: 136px; border-radius: 8px; border: 1.5px solid ${theme.cardBorder};
  background: ${theme.cardBg}; display: flex; align-items: center; justify-content: center; color: ${theme.textMuted};
`;

const PopupRight = styled.div`
  flex: 1; display: flex; flex-direction: column; overflow: hidden; min-width: 0;
`;

const PopupRightTop = styled.div`
  padding: 18px 18px 12px; border-bottom: 1px solid ${theme.separator}; flex-shrink: 0;
`;

const PopupNoteTitle = styled.h3`
  font-size: 17px; font-weight: 700; color: ${theme.textMain}; margin: 0 0 5px; letter-spacing: -0.2px;
`;

const PopupNoteDesc = styled.p`
  font-size: 13px; color: ${theme.textMuted}; margin: 0; line-height: 1.6;
`;

const PopupContentArea = styled.div`
  flex: 1; overflow-y: auto; padding: 14px 18px;
  font-size: 13px; color: ${theme.textMuted}; line-height: 1.7; white-space: pre-wrap; word-break: break-word;
`;

const PopupActions = styled.div`
  border-top: 1px solid ${theme.separator}; padding: 12px 18px 14px; display: flex; flex-direction: column; gap: 8px; flex-shrink: 0;
`;

const PopupOpenBtn = styled.a`
  display: flex; align-items: center; justify-content: center; gap: 7px;
  background: ${theme.primary}; color: #fff; border: none; border-radius: 8px;
  padding: 10px; font-size: 13px; font-weight: 600; font-family: inherit;
  text-decoration: none; cursor: pointer; transition: background 0.15s;
  &:hover { background: ${theme.primaryDark}; }
`;

const PopupCopyBtn = styled.button`
  display: flex; align-items: center; gap: 6px; background: transparent;
  color: ${theme.textMuted}; border: none; padding: 2px 0; font-size: 12px;
  font-weight: 500; font-family: inherit; cursor: pointer; transition: color 0.15s;
  &:hover { color: ${theme.primary}; }
`;

const STATIC_URL = (process.env.REACT_APP_BASE_URL || "http://localhost:8000/api").replace("/api", "");

function timeAgo(dateStr) {
  if (!dateStr) return null;
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function NoteCard({ note, onCommentClick, commentCount = 0, onDeleted, onEdited, readOnly = false }) {
  const currentUser = useSelector((s) => s.user.currentUser);
  const showToast = useToast();
  const [likes, setLikes] = useState(note.likes || []);
  const [views, setViews] = useState(note.views || []);
  const [imgError, setImgError] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(note.notename);
  const [editDesc, setEditDesc] = useState(note.desc || "");
  const [editSaving, setEditSaving] = useState(false);
  const [currentNote, setCurrentNote] = useState(note);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [copyDone, setCopyDone] = useState(false);
  const [ctxMenu, setCtxMenu] = useState(null);
  const [folders, setFolders] = useState([]);
  const [foldersLoaded, setFoldersLoaded] = useState(false);
  const [addingToFolder, setAddingToFolder] = useState(null);
  const ctxRef = useRef(null);

  const [ratings, setRatings] = useState(note.ratings || []);
  const editableFolders = folders.filter(
    (f) => f.accessRole === "owner" || f.accessRole === "editor"
  );
  const hasRated = currentUser ? !!ratings.find((r) => r.userId === currentUser._id) : false;
  const avgRating = ratings.length > 0
    ? Math.round((ratings.reduce((s, r) => s + r.value, 0) / ratings.length) * 10) / 10
    : null;

  const handleRate = async (_, value) => {
    if (!currentUser || !value) return;
    try {
      const res = await publicRequest.put(`/notes/${note._id}/rate`, { userId: currentUser._id, value });
      setRatings(res.data.ratings);
    } catch { /* silent */ }
  };

  const isOwner = currentUser && currentUser._id === note.userId;
  const isLiked = currentUser && likes.includes(currentUser._id);

  const thumbnailSrc = currentNote.thumbnailfilename && !imgError
    ? `${STATIC_URL}/images/${currentNote.thumbnailfilename}` : null;
  const noteFileSrc = currentNote.notefilename
    ? `${STATIC_URL}/images/${currentNote.notefilename}` : null;

  const handleView = () => {
    setPreviewOpen(true);
    if (!currentUser || views.includes(currentUser._id)) return;
    publicRequest.put(`/notes/${note._id}/view`, { userId: currentUser._id })
      .then(() => setViews((p) => [...p, currentUser._id])).catch(() => {});
  };

  useEffect(() => {
    if (!ctxMenu) return;
    const h = (e) => { if (ctxRef.current && !ctxRef.current.contains(e.target)) setCtxMenu(null); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [ctxMenu]);

  const loadFolders = useCallback(async () => {
    if (foldersLoaded || !currentUser) return;
    try { const res = await publicRequest.get(`/folders/${currentUser._id}`); setFolders(res.data); setFoldersLoaded(true); }
    catch { setFolders([]); }
  }, [currentUser, foldersLoaded]);

  const handleContextMenu = (e) => {
    e.preventDefault();
    loadFolders();
    // Clamp so menu never goes off-screen
    const menuW = 220;
    const menuH = 200;
    const x = Math.min(e.clientX, window.innerWidth - menuW - 8);
    const y = Math.min(e.clientY, window.innerHeight - menuH - 8);
    setCtxMenu({ x, y });
  };

  const handleAddToFolder = async (folderId) => {
    setAddingToFolder(folderId);
    try {
      await publicRequest.put(`/folders/${folderId}/add`, { userId: currentUser._id, noteId: note._id });
      setFolders((p) => p.map((f) => f._id === folderId && !f.noteIds.includes(note._id) ? { ...f, noteIds: [...f.noteIds, note._id] } : f));
    } catch {} finally { setAddingToFolder(null); setCtxMenu(null); }
  };

  const handleLike = async () => {
    if (!currentUser) return;
    const wasLiked = likes.includes(currentUser._id);
    setLikes(wasLiked ? likes.filter((id) => id !== currentUser._id) : [...likes, currentUser._id]);
    try { await publicRequest.put(`/notes/${note._id}/like`, { userId: currentUser._id }); }
    catch { setLikes(wasLiked ? [...likes] : likes.filter((id) => id !== currentUser._id)); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${currentNote.notename}"?`)) return;
    try {
      await publicRequest.delete(`/notes/${note._id}`, { data: { userId: currentUser._id } });
      showToast("Note deleted.");
      if (onDeleted) onDeleted(note._id);
    } catch { alert("Failed to delete note."); }
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    if (!editTitle.trim()) return;
    setEditSaving(true);
    try {
      const fd = new FormData();
      fd.append("userId", currentUser._id);
      fd.append("notename", editTitle.trim());
      fd.append("desc", editDesc.trim());
      const res = await publicRequest.put(`/notes/${note._id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCurrentNote(res.data); setEditing(false);
      if (onEdited) onEdited(res.data);
    } catch { alert("Failed to save changes."); }
    finally { setEditSaving(false); }
  };

  const handleCopyLink = () => {
    if (!noteFileSrc) return;
    navigator.clipboard.writeText(noteFileSrc).then(() => { setCopyDone(true); setTimeout(() => setCopyDone(false), 2000); });
  };

  const author = note.author;
  const authorInitial = author?.username?.[0]?.toUpperCase() || "?";
  const authorAvatarSrc = author?.profilePicture
    ? `${STATIC_URL}/images/${author.profilePicture}` : null;

  return (
    <>
      <Card onContextMenu={handleContextMenu}>
        {/* Author */}
        <AuthorRow>
          <AuthorLeft to={`/profile/${note.userId}`}>
            <Avatar>
              {authorAvatarSrc
                ? <AvatarImg src={authorAvatarSrc} alt={authorInitial} />
                : authorInitial}
            </Avatar>
            <AuthorName>{author?.username || "Unknown"}</AuthorName>
          </AuthorLeft>
          {isOwner && !readOnly && (
            <OwnerActions>
              <SmallBtn onClick={() => { setEditing((v) => !v); setEditTitle(currentNote.notename); setEditDesc(currentNote.desc || ""); }}>
                <IconEdit size="10px" /> Edit
              </SmallBtn>
              <SmallBtn $danger onClick={handleDelete}>
                <IconTrash size="10px" /> Delete
              </SmallBtn>
            </OwnerActions>
          )}
        </AuthorRow>

        {/* Thumbnail */}
        {thumbnailSrc ? (
          <Thumbnail src={thumbnailSrc} alt={currentNote.notename} onError={() => setImgError(true)} />
        ) : (
          <ThumbnailPlaceholder><IconDocument size="34px" /></ThumbnailPlaceholder>
        )}

        {/* Body */}
        <Body>
          <NoteTitle>{currentNote.notename}</NoteTitle>
          {currentNote.desc && <NoteDesc>{currentNote.desc}</NoteDesc>}
        </Body>

        {/* Edit form */}
        {editing && (
          <EditForm onSubmit={handleEditSave}>
            <EditInput value={editTitle} onChange={(e) => setEditTitle(e.target.value)} placeholder="Title" required />
            <EditTextarea value={editDesc} onChange={(e) => setEditDesc(e.target.value)} placeholder="Description (optional)" />
            <EditRow>
              <CancelBtn type="button" onClick={() => setEditing(false)}>Cancel</CancelBtn>
              <SaveBtn type="submit" disabled={editSaving || !editTitle.trim()}>{editSaving ? "Saving…" : "Save"}</SaveBtn>
            </EditRow>
          </EditForm>
        )}

        {/* Footer */}
        <Footer>
          <Stat $heart onClick={handleLike} $active={isLiked}>
            <IconHeart size="13px" filled={isLiked} /> {likes.length}
          </Stat>
          <Stat $comment onClick={() => onCommentClick && onCommentClick(note._id)}>
            <IconMessage size="13px" /> {commentCount}
          </Stat>
          <Stat>
            <IconEye size="13px" /> {views.length}
          </Stat>

          {/* Rating badge */}
          {(hasRated || avgRating !== null) && (
            <RatingBadge>
              ★ {avgRating ?? "—"}
            </RatingBadge>
          )}

          {timeAgo(currentNote.createdAt) && (
            <TimeStamp>
              <IconClock size="11px" />
              {timeAgo(currentNote.createdAt)}
            </TimeStamp>
          )}

          {noteFileSrc && (
            <ViewBtn onClick={handleView}>
              <IconDocument size="12px" /> View
            </ViewBtn>
          )}
        </Footer>

        {/* Rate row — only shown if not yet rated */}
        {currentUser && !hasRated && (
          <RateRow>
            <RateLabel>Rate:</RateLabel>
            <Rating
              name={`rate-${note._id}`}
              defaultValue={0}
              precision={0.5}
              size="small"
              onChange={handleRate}
              sx={{ color: theme.primary, "& .MuiRating-iconEmpty": { color: theme.cardBorderStrong } }}
            />
          </RateRow>
        )}
      </Card>

      {/* Context menu — portal so it's never clipped */}
      {ctxMenu && createPortal(
        <CtxMenu ref={ctxRef} style={{ top: ctxMenu.y, left: ctxMenu.x }}>
          {editableFolders.length > 0 ? (
            <>
              <CtxTitle><IconFolder size="11px" /> Add to folder</CtxTitle>
              <CtxDivider />
              {editableFolders.map((folder) => {
                const alreadyIn = folder.noteIds.includes(note._id);
                return (
                  <CtxItem key={folder._id} onClick={() => !alreadyIn && handleAddToFolder(folder._id)} disabled={alreadyIn || addingToFolder === folder._id}>
                    <IconFolder size="13px" /> {folder.name}
                    {alreadyIn && <span style={{ fontSize: 11, color: theme.textMuted, marginLeft: "auto" }}>Added</span>}
                    {addingToFolder === folder._id && <span style={{ fontSize: 11, color: theme.primary, marginLeft: "auto" }}>Adding…</span>}
                  </CtxItem>
                );
              })}
            </>
          ) : (
            <CtxItem disabled>No editable folders</CtxItem>
          )}
        </CtxMenu>,
        document.body
      )}

      {/* Preview popup — rendered via portal so it escapes card overflow:hidden */}
      {previewOpen && createPortal(
        <PopupOverlay onClick={() => setPreviewOpen(false)}>
          <PopupBox onClick={(e) => e.stopPropagation()}>
            <PopupBody>
              <PopupLeft>
                {thumbnailSrc ? <PopupThumb src={thumbnailSrc} alt={currentNote.notename} /> : <PopupThumbPlaceholder><IconDocument size="40px" /></PopupThumbPlaceholder>}
                <span style={{ fontSize: 10, fontWeight: 700, color: theme.textMuted, textTransform: "uppercase", letterSpacing: "0.8px" }}>Thumbnail</span>
                <span style={{ fontSize: 11, color: theme.textMuted, textAlign: "center", wordBreak: "break-all", display: "flex", alignItems: "center", gap: 4 }}>
                  <IconPaperclip size="11px" />
                  {currentNote.notefilename ? currentNote.notefilename.replace(/^\d+/, "").toUpperCase() : "No file"}
                </span>
              </PopupLeft>
              <PopupRight>
                <PopupRightTop>
                  <PopupNoteTitle>{currentNote.notename}</PopupNoteTitle>
                  {currentNote.desc && <PopupNoteDesc>{currentNote.desc}</PopupNoteDesc>}
                </PopupRightTop>
                <PopupContentArea>
                  {currentNote.desc || "No description provided for this note."}
                </PopupContentArea>
                <PopupActions>
                  <PopupOpenBtn href={noteFileSrc} target="_blank" rel="noopener noreferrer">
                    <IconExternalLink size="14px" /> Open Full File
                  </PopupOpenBtn>
                  <PopupCopyBtn onClick={handleCopyLink}>
                    {copyDone ? <IconCheck size="13px" /> : <IconClipboard size="13px" />}
                    {copyDone ? "Copied!" : "Copy File Link"}
                  </PopupCopyBtn>
                </PopupActions>
              </PopupRight>
            </PopupBody>
          </PopupBox>
        </PopupOverlay>,
        document.body
      )}
    </>
  );
}
